import { createContext, useContext, useState, useEffect } from "react";
import { account } from "../services/appwrite";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const userData = await account.get();
      setUser(userData);
      return userData;
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      await account.createEmailPasswordSession(email, password);
      const userData = await account.get();
      setUser(userData);
      setError(null);
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);

      // Get the current hostname
      const hostname = window.location.origin;
      console.log("Current hostname:", hostname);

      // Set up the redirect URLs
      const redirectUrl = `${hostname}/auth/callback`;
      const failureUrl = `${hostname}/login`;

      // Log the URLs for debugging
      console.log("Redirect URL:", redirectUrl);
      console.log("Failure URL:", failureUrl);

      // Make sure we're using HTTPS in production
      if (hostname.includes("vercel.app") && !hostname.startsWith("https")) {
        throw new Error("HTTPS is required for OAuth in production");
      }

      await account.createOAuth2Session("google", redirectUrl, failureUrl, [
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/userinfo.profile",
      ]);
    } catch (error) {
      console.error("Google OAuth error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email, password, name) => {
    try {
      setLoading(true);
      await account.create("unique()", email, password, name);
      await login(email, password);
      setError(null);
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await account.deleteSessions();
      setUser(null);
      setError(null);
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    signup,
    logout,
    checkUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
